import { orderHsitoryTypeConstants } from "constants/orderHistory";
import { ToastContext } from "context/ToastContext";
import { dollarsToCents } from "helpers/price";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { useContext, useState } from "react";
import { useMarkPaidViaCashUnmanagedMutation } from "store/queries/order";

import { ContainerForm } from "../PaymentSection/styled";
import { StyledNotesTextarea } from "./styled";

const paymentTypeOptions = [
  { label: "Cash", value: "Cash" },
  { label: "Check", value: "Check" },
  { label: "Financing", value: "CareCredit" },
];

export function PayCashUnmanaged({
  clearPrice,
  isInternalUser,
  orderId,
  onUpdateOrder,
  captchaToken,
  directPayLinkId,
  setShowPaymentSection,
  showOrderPaymentNotesEnabled,
}: {
  clearPrice: number;
  isInternalUser: boolean;
  orderId: string;
  onUpdateOrder?: () => void;
  captchaToken?: string;
  directPayLinkId?: string;
  setShowPaymentSection: any;
  showOrderPaymentNotesEnabled: boolean;
}) {
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [amount] = useState<number | null>(clearPrice);
  const [paymentType, setPaymentType] = useState<string | null>(null);
  const [markPaidViaCashUnmanaged, { isLoading: isMarkingPaid }] =
    useMarkPaidViaCashUnmanagedMutation();
  const { current: toastElement } = useContext(ToastContext);

  const isMainNotesFilled = notes.trim().length > 0;
  const hasValidPayment = paymentType != null;
  const isMarkPaidEnabled =
    isMainNotesFilled &&
    confirmed &&
    (showOrderPaymentNotesEnabled ? hasValidPayment : true);

  const handleMarkPaid = async () => {
    if (!isMarkPaidEnabled) return;
    if (showOrderPaymentNotesEnabled && paymentType == null) return;
    const paymentRecords = showOrderPaymentNotesEnabled
      ? [
          {
            orderId,
            type: orderHsitoryTypeConstants.ExternalPaymentRecorded,
            note: notes.trim(),
            amountInCents: dollarsToCents(amount ?? 0),
            externalPaymentMethod: paymentType ?? "",
          },
        ]
      : undefined;
    try {
      await markPaidViaCashUnmanaged({
        orderId,
        note: undefined,
        ...(!isInternalUser && {
          ...(captchaToken && { captchaToken }),
          ...(directPayLinkId && { directPayLinkId }),
        }),
        paymentRecords,
        isDirectPay: true,
      }).unwrap();
      onUpdateOrder?.();
      setShowPaymentSection({
        paymentPlan: false,
        paymentSection: false,
        paymentSuccess: true,
        receiptAvailable: false,
      });
      toastElement?.show({
        summary: "Success",
        severity: "success",
        detail: "Estimate has been marked paid",
      });
    } catch (error) {
      toastElement?.show({
        severity: "error",
        detail: "Failed to mark estimate as paid. Please try again.",
        summary: "Something went wrong.",
      });
    }
  };

  return (
    <ContainerForm>
      <div className="flex flex-column">
        <span className="title">
          <span>Mark paid via cash</span>
        </span>
      </div>
      {showOrderPaymentNotesEnabled && (
        <div className="flex gap-2 mt-4">
          <div className="flex flex-column gap-1 flex-1">
            <label
              htmlFor="paymentType"
              className="text-sm font-semibold black"
            >
              Payment Type
            </label>
            <Dropdown
              inputId="paymentType"
              value={paymentType}
              onChange={(e) => setPaymentType(e.value)}
              options={paymentTypeOptions}
              placeholder="Select type"
              className="w-full"
            />
          </div>
        </div>
      )}
      <div className="flex flex-column mt-4">
        <label htmlFor="notes" className="font-semibold black text-sm mb-2">
          Notes (Required):
        </label>
        <StyledNotesTextarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Enter payment details, receipt number, or other relevant information..."
          className="w-full"
        />
      </div>
      <div className="flex align-items-start gap-2 mt-4">
        <Checkbox
          inputId="confirm"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.checked ?? false)}
        />
        <label htmlFor="confirm" className="cursor-pointer text-sm">
          I confirm that the payment has been collected and I understand Clear
          Health will not process this payment.
        </label>
      </div>
      <Button
        label="Mark paid"
        className="w-full justify-content-center border-round-lg mt-6"
        disabled={!isMarkPaidEnabled || isMarkingPaid}
        loading={isMarkingPaid}
        onClick={handleMarkPaid}
      />
    </ContainerForm>
  );
}
